import { useTranslation } from "next-i18next";
import React, { useEffect, useState } from "react";

import { GiftCardDownloadDetails } from "@/components/gift-cards/gift-card-download-details/gift-card-download-details";

import { GiftCardProps } from "@/interfaces/gift-cards/gift-card-interface";

import { Locale } from "@/types/locale";

import InfoIcon from "../../assets/icons/Info-icon.svg";
import Accordion from "../accordion/accordion";
import ActionButton from "../buttons/action-button";
import { KINDS as BUTTON_KINDS, SIZES as BUTTON_SIZES } from "../buttons/buttons.constants";
import { DropdownInput } from "../dropdown-inputs/dropdown-input";
import {
    KINDS as DROPDOWN_KINDS,
    SIZES as DROPDOWN_SIZES,
} from "../dropdown-inputs/dropdown-input.constants";
import { DropdownMenuItem } from "../dropdown-menus/dropdown-menu-items/dropdown-menu-item-interface";
import styles from "./gift-card-details.module.scss";
import LearnMore from "./learn-more";

interface Props {
    giftCard: GiftCardProps;
    menuItems: Array<DropdownMenuItem>;
    selectedAmount: string;
    setSelectedAmount: (amount: string) => void;
    onBuyForYourself: () => void;
    locale: Locale;
    onBuyForAFriend: () => void;
}

const GiftCardDetails: React.FC<Props> = ({
    giftCard,
    menuItems,
    selectedAmount,
    setSelectedAmount,
    onBuyForYourself,
    onBuyForAFriend,
    locale,
}) => {
    const { inStoreOnly, expiry, details, terms } = giftCard;
    const [showOldOrdering, setShowOldOrdering] = useState(true);

    const setSelectedGiftCardAmount = (item: DropdownMenuItem) => {
        setSelectedAmount(item.name);
    };
    const { t } = useTranslation();

    useEffect(() => {
        setShowOldOrdering(!!window["AfterpayReactNativeWebView"]);
    }, []);

    const giftCardDetails = (
        <>
            <div
                className={styles.giftCardDenominationsContainer}
                data-testid="gift-card-denominations"
            >
                <DropdownInput
                    dropdownMenuItems={menuItems}
                    kind={DROPDOWN_KINDS.TEXT}
                    placeholder={selectedAmount}
                    size={DROPDOWN_SIZES.REGULAR}
                    onClick={setSelectedGiftCardAmount}
                    dropdownOnly={true}
                />
            </div>
            <div className={styles.learnMoreContainer}>
                <LearnMore price={selectedAmount} />
            </div>
            {inStoreOnly && (
                <div className={styles.inStoreContentContainer}>
                    <InfoIcon />
                    <p>{t("giftCards.inStore.content")}</p>
                </div>
            )}
            <div className={styles.buyForYourselfContainer}>
                <ActionButton
                    disabled={false}
                    fluid={true}
                    kind={BUTTON_KINDS.PRIMARY}
                    label={t("giftCards.buttons.notGift")}
                    onClick={onBuyForYourself}
                    size={BUTTON_SIZES.LARGE}
                />
            </div>
            <div className={styles.sendToFriendContainer}>
                <ActionButton
                    disabled={false}
                    fluid={true}
                    kind={BUTTON_KINDS.PRIMARY}
                    label={t("giftCards.buttons.gift")}
                    onClick={onBuyForAFriend}
                    size={BUTTON_SIZES.LARGE}
                />
            </div>
        </>
    );

    const outOfStock = (
        <span className={styles.outOfStockContainer}>{t("giftCards.outOfStock")}</span>
    );

    const renderGiftCardDetailsHeader = () => {
        if (showOldOrdering === true) {
            return menuItems.length ? giftCardDetails : outOfStock;
        }

        if (showOldOrdering === false) {
            return <GiftCardDownloadDetails locale={locale} />;
        }

        return null;
    };

    return (
        <>
            {renderGiftCardDetailsHeader()}

            <div className={styles.detailsContainer}>
                <Accordion title={t("giftCards.accordions.details")} content={details} />
            </div>
            <div className={styles.expirationContainer}>
                <Accordion title={t("giftCards.accordions.expiration")} content={expiry} />
            </div>
            <div className={styles.termsContainer}>
                <Accordion title={t("giftCards.accordions.termsAndConditions")} content={terms} />
            </div>
        </>
    );
};

export default GiftCardDetails;
